import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  SESSION_ACCESS_KEY,
  SESSION_REFRESH_KEY,
} from '@/config/constants';
import type { AuthTokens, UserProfile } from '@/types/api';

interface AuthContextType {
  // State
  user: UserProfile | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (tokens: AuthTokens, user: UserProfile) => void;
  logout: () => void;
  updateUser: (userData: Partial<UserProfile>) => void;
  updateTokens: (tokens: AuthTokens) => void;

  // Utilities
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider for QPS Application
 * Manages user authentication state, tokens, and permissions
 * Integrates with Laravel Sanctum authentication
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Clear all stored authentication data
   */
  const clearStoredAuth = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    sessionStorage.removeItem(SESSION_ACCESS_KEY);
    sessionStorage.removeItem(SESSION_REFRESH_KEY);
  }, []);

  /**
   * Login function - stores tokens and user data
   */
  const login = useCallback((newTokens: AuthTokens, userData: UserProfile) => {
    try {
      setTokens(newTokens);
      setUser(userData);

      // Persist to storage
      localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(newTokens));
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));

      // Quick access via session storage
      sessionStorage.setItem(SESSION_ACCESS_KEY, newTokens.access);
      sessionStorage.setItem(SESSION_REFRESH_KEY, newTokens.refresh);
    } catch (error) {
      console.error('Failed to process login:', error);
    }
  }, []);

  /**
   * Logout function - clears all auth data and redirects
   */
  const logout = useCallback(() => {
    setTokens(null);
    setUser(null);
    clearStoredAuth();

    // Redirect to login page
    navigate('/login', { replace: true });
  }, [navigate, clearStoredAuth]);

  /**
   * Initialize auth state from storage on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Try to load stored authentication data
        const storedTokens = localStorage.getItem(AUTH_TOKEN_KEY);
        const storedUser = localStorage.getItem(AUTH_USER_KEY);

        if (storedTokens && storedUser) {
          const parsedTokens: AuthTokens = JSON.parse(storedTokens);
          const parsedUser: UserProfile = JSON.parse(storedUser);

          // Validate that we have required data
          if (parsedTokens.access && parsedUser.id) {
            setTokens(parsedTokens);
            setUser(parsedUser);

            // Also set session storage for quick access
            sessionStorage.setItem(SESSION_ACCESS_KEY, parsedTokens.access);
            if (parsedTokens.refresh) {
              sessionStorage.setItem(SESSION_REFRESH_KEY, parsedTokens.refresh);
            }
          } else {
            // Clear invalid data
            clearStoredAuth();
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth from storage:', error);
        // Clear corrupted data
        clearStoredAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [clearStoredAuth]);

  /**
   * Listen for authentication expiration events from API error handler
   */
  useEffect(() => {
    const handleAuthExpired = () => {
      // Call logout logic directly to avoid circular dependency
      setTokens(null);
      setUser(null);

      // Clear stored auth data
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      sessionStorage.removeItem(SESSION_ACCESS_KEY);
      sessionStorage.removeItem(SESSION_REFRESH_KEY);

      // Redirect to login
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [navigate]);

  /**
   * Update user profile data
   */
  const updateUser = useCallback(
    (userData: Partial<UserProfile>) => {
      if (user) {
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);

        try {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(updatedUser));
        } catch (error) {
          console.error('Failed to update stored user data:', error);
        }
      }
    },
    [user]
  );

  /**
   * Update authentication tokens
   */
  const updateTokens = useCallback((newTokens: AuthTokens) => {
    setTokens(newTokens);

    try {
      localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(newTokens));
      sessionStorage.setItem(SESSION_ACCESS_KEY, newTokens.access);
      sessionStorage.setItem(SESSION_REFRESH_KEY, newTokens.refresh);
    } catch (error) {
      console.error('Failed to update stored tokens:', error);
    }
  }, []);

  /**
   * Check if user has specific permission
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user || !user.permissions) return false;
      return user.permissions.includes(permission);
    },
    [user]
  );

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      if (!user || !user.roles) return false;
      return user.roles.includes(role);
    },
    [user]
  );

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (!user || !user.roles) return false;
      return roles.some(role => user.roles.includes(role));
    },
    [user]
  );

  // Calculate authentication status
  const isAuthenticated = !!(user && tokens?.access);

  const contextValue: AuthContextType = {
    // State
    user,
    tokens,
    isAuthenticated,
    isLoading,

    // Actions
    login,
    logout,
    updateUser,
    updateTokens,

    // Utilities
    hasPermission,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
